/**
 * The permission registry — the single source of truth for authorisation.
 *
 * CLAUDE.md §9: every controller method carries `@Permissions('module.action')`,
 * and a route with no permission code is rejected by the global guard.
 * CLAUDE.md §11: roles are bundles of these codes, editable at runtime; code
 * checks permissions, never role names.
 *
 * Codes are append-only. Renaming one silently un-grants it from every live role
 * in `role_permissions`, because that table stores the string, not a foreign key.
 */
export const PERMISSIONS = {
  // ── accounting (Phase 5) ───────────────────────────────────────────
  // Read-only toward AutoCount (CLAUDE.md §13). None of these permit a write
  // to NKS's books; they govern OUR handover records and the reconciliation.
  HANDOVER_VIEW: 'accounting.handover_view',
  /// Freezing a batch is irreversible — §8 says a correction is a NEW batch,
  /// never an edit — so it is separated from merely looking at one.
  HANDOVER_RELEASE: 'accounting.handover_release',
  /// Which of our customers maps to which AutoCount debtor. A wrong mapping
  /// files a document against the wrong person's AR ageing, so this is not
  /// bundled with ordinary customer editing.
  ACCOUNTING_MAP_VIEW: 'accounting.map_view',
  ACCOUNTING_MAP_MANAGE: 'accounting.map_manage',
  RECONCILIATION_VIEW: 'accounting.reconciliation_view',
  ANALYTICS_VIEW: 'analytics.view',
  ANALYTICS_CONFIGURE: 'analytics.configure',
  /// Receives the 08:00 exceptions digest (spec 2026-10-29). Separate from
  /// viewing, so who is told each morning is a choice, not everyone who reads Insights.
  ANALYTICS_DIGEST: 'analytics.digest',

  /// Declaring which AutoCount item code is which bike model (Q85). Separate
  /// from unit editing: a wrong mapping does not damage one unit, it files
  /// every future unit of that code under the wrong model.
  MODEL_MAP_VIEW: 'vehicles.model_map_view',
  MODEL_MAP_MANAGE: 'vehicles.model_map_manage',
  /**
   * Correcting a match that was already applied (Q85).
   *
   * Separate from MODEL_MAP_MANAGE because the stakes differ: declaring an
   * unmatched code creates records, while correcting a matched one REWRITES
   * records that already exist and may already be on a quotation. Same screen,
   * different authority.
   */
  MODEL_MAP_REMAP: 'vehicles.model_map_remap',

  // ── iam ────────────────────────────────────────────────────────────
  USER_VIEW: 'iam.user_view',
  USER_MANAGE: 'iam.user_manage',
  ROLE_VIEW: 'iam.role_view',
  ROLE_MANAGE: 'iam.role_manage',
  BRANCH_VIEW: 'iam.branch_view',
  BRANCH_MANAGE: 'iam.branch_manage',
  SESSION_REVOKE: 'iam.session_revoke',
  AUDIT_VIEW: 'iam.audit_view',

  /**
   * Bypasses BranchScopeService's automatic `branch_id` filter (CLAUDE.md §7.7).
   * Deliberately namespaced `branch.` rather than `iam.` because it is a data-scope
   * capability, not an administrative one.
   */
  BRANCH_VIEW_ALL: 'branch.view_all',

  // ── customers ──────────────────────────────────────────────────────
  CUSTOMER_VIEW: 'customer.view',
  CUSTOMER_CREATE: 'customer.create',
  CUSTOMER_UPDATE: 'customer.update',
  CUSTOMER_MERGE: 'customer.merge',
  /** Unmasks NRIC/passport. Every read through this code is audited (CLAUDE.md §11). */
  CUSTOMER_VIEW_PII: 'customer.view_pii',
  CUSTOMER_EXPORT: 'customer.export',
  CONSENT_CAPTURE: 'customer.consent_capture',

  // ── crm ────────────────────────────────────────────────────────────
  LEAD_VIEW: 'crm.lead_view',
  LEAD_CREATE: 'crm.lead_create',
  LEAD_UPDATE: 'crm.lead_update',
  /// Reassign a lead away from its current advisor. A manager decision:
  /// advisors are measured on what they close.
  LEAD_ASSIGN: 'crm.lead_assign',
  LEAD_EXPORT: 'crm.lead_export',
  ACTIVITY_LOG: 'crm.activity_log',
  STAGE_ADVANCE: 'crm.stage_advance',
  /// Move a lead BACKWARD through the pipeline. Forward skips are ordinary;
  /// walking a lead back is how a funnel gets massaged, so it is audited.
  REOPEN_STAGE: 'crm.reopen_stage',

  // ── sales ──────────────────────────────────────────────────────────
  QUOTATION_VIEW: 'sales.quotation_view',
  QUOTATION_CREATE: 'sales.quotation_create',
  QUOTATION_UPDATE: 'sales.quotation_update',
  QUOTATION_SEND: 'sales.quotation_send',
  QUOTATION_CANCEL: 'sales.quotation_cancel',
  PRICE_LIST_MANAGE: 'sales.price_list_manage',
  /// Authorise a discount up to 5% without a second signature (Q10).
  DISCOUNT_APPROVE_5: 'sales.discount_approve_5',
  /// Authorise any discount. Management only.
  DISCOUNT_APPROVE_ANY: 'sales.discount_approve_any',
  /// Take a motorcycle that is already reserved for another customer.
  /// CLAUDE.md §8 requires a reason, and it is audited.
  OVERRIDE_ALLOCATION: 'sales.override_allocation',

  // ── vehicles ───────────────────────────────────────────────────────
  UNIT_VIEW: 'vehicles.unit_view',
  UNIT_RECEIVE: 'vehicles.unit_receive',
  UNIT_MOVE: 'vehicles.unit_move',
  UNIT_STATUS_CHANGE: 'vehicles.unit_status_change',
  LOCATION_MANAGE: 'vehicles.location_manage',

  BOOKING_VIEW: 'sales.booking_view',
  BOOKING_CREATE: 'sales.booking_create',
  /// Reserve a motorcycle and send its deposit to the cashier. The cashier's receipt reserves it.
  BOOKING_CONFIRM: 'sales.booking_confirm',
  /// Cancel a booking. Money is kept or returned, so it is a manager decision.
  BOOKING_CANCEL: 'sales.booking_cancel',
  /// Bill a motorcycle sale from its booking (spec 2026-10-05 §5.1): preview and
  /// issue. Bills motorcycles only — job cards and counter sales stay pos.invoice_issue.
  SALE_BILL: 'sales.sale_bill',

  FINANCING_VIEW: 'sales.financing_view',
  FINANCING_CREATE: 'sales.financing_create',
  /// Send the application to the bank. Gated on the document checklist.
  FINANCING_SUBMIT: 'sales.financing_submit',
  /// Record the bank's answer. Not a decision anyone here makes — but whoever
  /// keys it decides what the pipeline and the customer are told.
  FINANCING_DECIDE: 'sales.financing_decide',
  /// Tick off, waive or attach a required document.
  CHECKLIST_UPDATE: 'sales.checklist_update',
  /// Add or retire a bank. Configuration, not day-to-day work.
  FINANCING_PROVIDER_MANAGE: 'sales.financing_provider_manage',

  // ── registration & delivery ────────────────────────────────────────
  REGISTRATION_VIEW: 'vehicles.registration_view',
  /// Start AP/Puspakom/JPJ on an allocated motorcycle.
  REGISTRATION_OPEN: 'vehicles.registration_open',
  /// Move a stage, attach its documents, record a reference number.
  REGISTRATION_UPDATE: 'vehicles.registration_update',

  DELIVERY_VIEW: 'vehicles.delivery_view',
  DELIVERY_SCHEDULE: 'vehicles.delivery_schedule',
  /// Record a PDI result. The person who checked the brakes signs for it.
  PDI_RECORD: 'vehicles.pdi_record',
  /// Hand the motorcycle over. Irreversible, and the last gate before the
  /// customer rides away.
  DELIVERY_HANDOVER: 'vehicles.delivery_handover',

  // ── workshop ───────────────────────────────────────────────────────
  JOB_VIEW: 'workshop.job_view',
  JOB_CREATE: 'workshop.job_create',
  JOB_UPDATE: 'workshop.job_update',
  /// Move a job through the statuses, including the billing gate.
  JOB_STATUS_CHANGE: 'workshop.job_status_change',
  /// Put a technician on a job, or take them off it.
  JOB_ASSIGN: 'workshop.job_assign',
  /// Start and stop your own clock. Every technician has this.
  TIME_LOG: 'workshop.time_log',
  /// Record what the customer agreed to, and how. Not the technician's call.
  APPROVAL_RECORD: 'workshop.approval_record',
  /// Sign off the quality checks.
  QC_RECORD: 'workshop.qc_record',
  /// Bill fewer hours than were worked. Never more (CLAUDE.md §8).
  LABOUR_ADJUST: 'workshop.labour_adjust',
  /// Maintain grades and the labour rate card.
  LABOUR_RATE_MANAGE: 'workshop.labour_rate_manage',

  // ── parts & warehouse ──────────────────────────────────────────────
  SKU_VIEW: 'parts.sku_view',
  SKU_MANAGE: 'parts.sku_manage',
  STOCK_VIEW: 'parts.stock_view',
  /// Book goods in from a supplier.
  GOODS_RECEIVE: 'parts.goods_receive',
  /// Ask the warehouse for parts against a job card.
  PARTS_REQUEST: 'parts.request',
  /// Hand parts over. Moves stock and writes the job line the invoice bills.
  PARTS_ISSUE: 'parts.issue',
  /// Take unused parts back.
  PARTS_RETURN: 'parts.return',
  /// Correct a balance. Always with a reason, always audited.
  STOCK_ADJUST: 'parts.stock_adjust',
  /// Count the shelves. Blind: the counter never sees the system figure.
  STOCK_COUNT: 'parts.stock_count',
  /// Build, send and cancel a counter sale. Handing it over is PARTS_ISSUE.
  COUNTER_SELL: 'parts.counter_sell',

  // ── POS ────────────────────────────────────────────────────────────
  INVOICE_VIEW: 'pos.invoice_view',
  /// Generate an invoice FROM a job card. There is no way to type one.
  INVOICE_ISSUE: 'pos.invoice_issue',
  /// Reverse an invoice with a credit note. Never an edit.
  INVOICE_VOID: 'pos.invoice_void',
  PAYMENT_TAKE: 'pos.payment_take',
  /// Sign off a refund of money already taken. The approver may not pay it out.
  REFUND_APPROVE: 'pos.refund_approve',
  /// Hand the approved money back: cash from the drawer, card, or bank transfer.
  REFUND_PAY: 'pos.refund_pay',
  /// Open and close a till, and own its variance.
  CASHIER_SESSION: 'pos.cashier_session',

  // ── customer service ───────────────────────────────────────────────
  /// See cases, follow-up tasks and service reminders.
  CASE_VIEW: 'servicedesk.case_view',
  /// Open a case, respond, change its status. Walk-in complaints land at the counter.
  CASE_MANAGE: 'servicedesk.case_manage',
  /// Hand a case to someone else. Audited.
  CASE_ASSIGN: 'servicedesk.case_assign',
  /// Work follow-up calls: record the outcome, try again, create by hand.
  FOLLOWUP_MANAGE: 'servicedesk.followup_manage',
  /// Send a service reminder on WhatsApp, or dismiss it.
  REMINDER_SEND: 'servicedesk.reminder_send',
  /// Read the no-service and upgrade retention lists.
  RETENTION_VIEW: 'servicedesk.retention_view',
  /// Retune SLA targets and reminder intervals.
  SERVICE_DESK_CONFIGURE: 'servicedesk.configure',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSION_CODES: readonly PermissionCode[] = Object.freeze(
  Object.values(PERMISSIONS),
);

/** The ten roles from blueprint §17, via CLAUDE.md §11. */
export const ROLE_CODES = [
  'MANAGEMENT',
  'SALES_MANAGER',
  'SALES_ADVISOR',
  'SERVICE_ADVISOR',
  'TECHNICIAN',
  'WAREHOUSE',
  'CASHIER',
  'ACCOUNTS',
  'ADMIN',
  'SYSTEM_ADMIN',
] as const;

export type RoleCode = (typeof ROLE_CODES)[number];

const P = PERMISSIONS;

/** What anyone working the counter needs to do their job and nothing more. */
const CUSTOMER_DESK: readonly PermissionCode[] = [
  P.CUSTOMER_VIEW,
  P.CUSTOMER_CREATE,
  P.CUSTOMER_UPDATE,
  P.CONSENT_CAPTURE,
];

/** Quoting: available to anyone who works the pipeline. */
const QUOTATION_DESK: readonly PermissionCode[] = [
  P.QUOTATION_VIEW,
  P.QUOTATION_CREATE,
  P.QUOTATION_UPDATE,
  P.QUOTATION_SEND,
  P.BOOKING_VIEW,
  P.BOOKING_CREATE,
  P.BOOKING_CONFIRM,
  P.FINANCING_VIEW,
  P.FINANCING_CREATE,
  P.FINANCING_SUBMIT,
  P.CHECKLIST_UPDATE,
  // An advisor is the one the customer rings about their registration, so
  // they see it — but the clerk owns the stages.
  P.REGISTRATION_VIEW,
  P.DELIVERY_VIEW,
  P.DELIVERY_SCHEDULE,
  P.PDI_RECORD,
  P.DELIVERY_HANDOVER,
  // Bill this sale.
  P.SALE_BILL,
];

/** Seeing and moving stock: the showroom and warehouse floor. */
const UNIT_DESK: readonly PermissionCode[] = [P.UNIT_VIEW, P.UNIT_MOVE];

/** Working a pipeline: what a sales advisor does all day. */
const CRM_DESK: readonly PermissionCode[] = [
  P.LEAD_VIEW,
  P.LEAD_CREATE,
  P.LEAD_UPDATE,
  P.ACTIVITY_LOG,
  P.STAGE_ADVANCE,
];

/** Customer service for staff who face customers: cases, follow-up calls, reminders. */
const SERVICE_DESK_FRONT: readonly PermissionCode[] = [
  P.CASE_VIEW,
  P.CASE_MANAGE,
  P.FOLLOWUP_MANAGE,
  P.REMINDER_SEND,
];

/** Cashier and registration clerk take walk-in complaints: view and open cases only. */
const SERVICE_DESK_COUNTER: readonly PermissionCode[] = [P.CASE_VIEW, P.CASE_MANAGE];

/** Configuration rights only — CLAUDE.md §11 keeps SYSTEM_ADMIN out of business approvals. */
const SYSTEM_ADMIN_CODES: readonly PermissionCode[] = [
  P.USER_VIEW,
  P.USER_MANAGE,
  P.ROLE_VIEW,
  P.ROLE_MANAGE,
  P.BRANCH_VIEW,
  P.BRANCH_MANAGE,
  P.BRANCH_VIEW_ALL,
  P.SESSION_REVOKE,
  P.AUDIT_VIEW,
];

/**
 * Seed-time defaults. These are written into `role_permissions` by the seed script
 * and are editable at runtime afterwards — this object is the starting point, not
 * a runtime authority.
 */
export const ROLE_BUNDLES: Record<RoleCode, readonly PermissionCode[]> = {
  // Management sees everything but does not administer users or roles.
  // Management sees everything, administers nothing, and is the only role that
  // can sign off a discount above the manager band.
  MANAGEMENT: ALL_PERMISSION_CODES.filter(
    (code) => code !== P.USER_MANAGE && code !== P.ROLE_MANAGE,
  ),
  SALES_MANAGER: [
    // Q85: whoever keeps the model catalogue declares the AutoCount mapping.
    P.MODEL_MAP_VIEW,
    P.MODEL_MAP_MANAGE,
    P.MODEL_MAP_REMAP,
    ...CUSTOMER_DESK,
    ...CRM_DESK,
    ...QUOTATION_DESK,
    P.LEAD_ASSIGN,
    P.LEAD_EXPORT,
    P.REOPEN_STAGE,
    ...UNIT_DESK,
    P.UNIT_RECEIVE,
    P.UNIT_STATUS_CHANGE,
    P.LOCATION_MANAGE,
    // Only a manager may take a bike promised to another customer (CLAUDE.md §8).
    P.OVERRIDE_ALLOCATION,
    P.QUOTATION_CANCEL,
    // Cancelling decides whether a customer's deposit is kept or returned.
    P.BOOKING_CANCEL,
    P.FINANCING_DECIDE,
    P.FINANCING_PROVIDER_MANAGE,
    P.REGISTRATION_OPEN,
    P.REGISTRATION_UPDATE,
    P.JOB_VIEW,
    P.JOB_STATUS_CHANGE,
    P.JOB_ASSIGN,
    P.LABOUR_ADJUST,
    P.LABOUR_RATE_MANAGE,
    P.SKU_VIEW,
    P.SKU_MANAGE,
    P.STOCK_VIEW,
    P.STOCK_ADJUST,
    P.STOCK_COUNT,
    P.INVOICE_VIEW,
    // Reversing an invoice is a manager's call, not a cashier's.
    P.INVOICE_VOID,
    P.REFUND_APPROVE,
    P.PRICE_LIST_MANAGE,
    // Q10 default: a manager signs off up to 5%; above that goes to management.
    P.DISCOUNT_APPROVE_5,
    P.CUSTOMER_MERGE,
    P.CUSTOMER_VIEW_PII,
    P.CUSTOMER_EXPORT,
    P.BRANCH_VIEW,
    P.USER_VIEW,
    ...SERVICE_DESK_FRONT,
    P.CASE_ASSIGN,
    P.RETENTION_VIEW,
  ],
  // Advisors quote at list price; any discount needs a manager (Q10).
  SALES_ADVISOR: [
    ...CUSTOMER_DESK,
    ...CRM_DESK,
    ...QUOTATION_DESK,
    ...UNIT_DESK,
    P.BRANCH_VIEW,
    ...SERVICE_DESK_FRONT,
  ],
  SERVICE_ADVISOR: [
    ...CUSTOMER_DESK,
    P.LEAD_VIEW,
    P.QUOTATION_VIEW,
    P.UNIT_VIEW,
    // The counter: opens the job, drives it, records what the customer agreed.
    P.JOB_VIEW,
    P.JOB_CREATE,
    P.JOB_UPDATE,
    P.JOB_STATUS_CHANGE,
    P.JOB_ASSIGN,
    P.APPROVAL_RECORD,
    P.QC_RECORD,
    // Billing DOWN from what was worked, with a reason. Never up.
    P.LABOUR_ADJUST,
    P.SKU_VIEW,
    P.STOCK_VIEW,
    P.PARTS_REQUEST,
    P.INVOICE_VIEW,
    P.INVOICE_ISSUE,
    P.BRANCH_VIEW,
    ...SERVICE_DESK_FRONT,
  ],
  TECHNICIAN: [
    P.CUSTOMER_VIEW,
    P.UNIT_VIEW,
    P.JOB_VIEW,
    // Their findings and their own clock — not the status, not the approval,
    // not what gets billed.
    P.JOB_UPDATE,
    P.TIME_LOG,
    P.QC_RECORD,
    // Ask the warehouse. Issuing to yourself is how stock walks.
    P.SKU_VIEW,
    P.STOCK_VIEW,
    P.PARTS_REQUEST,
    P.BRANCH_VIEW,
  ],
  WAREHOUSE: [
    ...UNIT_DESK,
    P.UNIT_RECEIVE,
    P.UNIT_STATUS_CHANGE,
    // The warehouse has the motorcycle, so the warehouse does the PDI.
    P.DELIVERY_VIEW,
    P.PDI_RECORD,
    P.REGISTRATION_VIEW,
    // And the stores.
    P.SKU_VIEW,
    P.STOCK_VIEW,
    P.GOODS_RECEIVE,
    P.PARTS_ISSUE,
    P.PARTS_RETURN,
    P.STOCK_COUNT,
    // The parts counter sells as well as issues (spec 2026-10-02).
    P.COUNTER_SELL,
    P.JOB_VIEW,
    P.BRANCH_VIEW,
  ],
  // The cashier READS the job card and bills from it. They cannot edit it —
  // CLAUDE.md §8: "Cashier cannot manually type parts or approved labour."
  CASHIER: [
    P.CUSTOMER_VIEW,
    P.JOB_VIEW,
    P.SKU_VIEW,
    P.STOCK_VIEW,
    P.INVOICE_VIEW,
    P.INVOICE_ISSUE,
    P.SALE_BILL,
    P.PAYMENT_TAKE,
    P.REFUND_PAY,
    P.CASHIER_SESSION,
    P.BRANCH_VIEW,
    ...SERVICE_DESK_COUNTER,
  ],
  ACCOUNTS: [
    // Phase 5. Releasing a handover batch is the Accounts desk's own work.
    P.HANDOVER_VIEW,
    P.HANDOVER_RELEASE,
    P.REFUND_PAY,
    P.ACCOUNTING_MAP_VIEW,
    P.ACCOUNTING_MAP_MANAGE,
    P.RECONCILIATION_VIEW,
    // Management reporting; Accounts reconciles the figures.
    P.ANALYTICS_VIEW,
    P.CUSTOMER_VIEW,
    P.CUSTOMER_EXPORT,
    P.QUOTATION_VIEW,
    P.BOOKING_VIEW,
    // Accounts needs to see what is financed and by whom to reconcile it.
    P.FINANCING_VIEW,
    P.INVOICE_VIEW,
    P.STOCK_VIEW,
    P.BRANCH_VIEW,
  ],
  // ADMIN here is the AP/Puspakom/JPJ registration clerk, not a system administrator.
  ADMIN: [
    // Q85: whoever keeps the model catalogue declares the AutoCount mapping.
    P.MODEL_MAP_VIEW,
    P.MODEL_MAP_MANAGE,
    P.MODEL_MAP_REMAP,
    ...CUSTOMER_DESK,
    ...UNIT_DESK,
    P.CUSTOMER_VIEW_PII,
    // ADMIN here is the registration clerk. This IS their job.
    P.REGISTRATION_VIEW,
    P.REGISTRATION_OPEN,
    P.REGISTRATION_UPDATE,
    P.JOB_VIEW,
    P.JOB_STATUS_CHANGE,
    P.JOB_ASSIGN,
    P.LABOUR_ADJUST,
    P.LABOUR_RATE_MANAGE,
    P.SKU_VIEW,
    P.SKU_MANAGE,
    P.STOCK_VIEW,
    P.INVOICE_VIEW,
    // Reversing an invoice is a manager's call, not a cashier's.
    P.INVOICE_VOID,
    P.CHECKLIST_UPDATE,
    P.UNIT_STATUS_CHANGE,
    P.DELIVERY_VIEW,
    P.DELIVERY_SCHEDULE,
    P.BRANCH_VIEW,
    ...SERVICE_DESK_COUNTER,
  ],
  SYSTEM_ADMIN: SYSTEM_ADMIN_CODES,
};
